import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { AnimatePresence } from 'framer-motion'
import React from 'react'
import useBreakpoint from 'src/common/hooks/ui/useBreakpoint'
import Fade from 'src/common/ui-kit/Animations/Fade'
import Badge, { BadgeProps } from 'src/common/ui-kit/Badge'
import DateTimePicker from 'src/common/ui-kit/Date/DateTimePicker'
import useClickAway from 'src/common/ui-kit/hooks/useClickAway'
import Modal from 'src/common/ui-kit/Modal'
import { customInitialDate, customMinDate } from './consts'
import { ExpiryBadgesProps } from './ExpiryBadges'
import { DateTimePickerProps } from 'src/common/ui-kit/Date/DateTimePicker'
import useToggle from 'src/common/hooks/helpers/useToggle'
import { FULL_DATE_TIME_FORMAT } from 'src/common/consts/dateFormats'

interface CustomDateBadgeProps
  extends ExpiryBadgesProps,
    Omit<BadgeProps<unknown>, 'onSelect'> {
  isActive: boolean
  forceMobileView?: boolean
  children?: React.ReactNode
  timeOptions?: DateTimePickerProps
}

const CustomDateBadge: React.FC<CustomDateBadgeProps> = ({
  expiryTimestamp,
  onSelect,
  className,
  isActive,
  forceMobileView,
  children,
  timeOptions,
  ...props
}) => {
  const { ref, active, toggle } = useClickAway()
  const { isOn: modalOpen, toggle: toggleModal } = useToggle()

  const isLg = useBreakpoint('lg')

  const updateCustomDate = (value: Dayjs) => {
    onSelect(value.unix())
    toggle()
    toggleModal()
  }

  const customDate = isActive ? dayjs.unix(expiryTimestamp) : customInitialDate

  const formattedDate = dayjs
    .unix(expiryTimestamp)
    .format(FULL_DATE_TIME_FORMAT)

  const viewModal = isLg || forceMobileView
  const modalProps = viewModal ? {} : { ref }

  const toggleDatePicker = () => {
    toggle()
    toggleModal()
  }

  return (
    <div className={clsx('inline-flex', className)} {...modalProps}>
      <Badge
        onClick={toggleDatePicker}
        color='secondary'
        active={isActive}
        size='sm'
        {...props}
      >
        {children || formattedDate}
      </Badge>

      {viewModal ? (
        <Modal
          className='sm:max-w-fit'
          open={modalOpen}
          onClose={() => toggleModal()}
        >
          <div className='flex justify-center'>
            <DateTimePicker
              className='shadow-none'
              minDate={customMinDate}
              initialDate={customDate}
              onDateChange={updateCustomDate}
              {...timeOptions}
            />
          </div>
        </Modal>
      ) : (
        <AnimatePresence>
          {active && (
            <Fade className='absolute z-10 top-0 -left-80' scale={0.9}>
              <DateTimePicker
                minDate={customMinDate}
                initialDate={customDate}
                onDateChange={updateCustomDate}
                {...timeOptions}
              />
            </Fade>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}

export default CustomDateBadge
